import { createFileRoute, Link } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

export const Route = createFileRoute('/terms')({
  component: TermsOfService,
});

function TermsOfService() {
  const { t } = useLingui();
  const lastUpdated = 'February 10, 2026';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span className="text-sm">
              <Trans>Back to TrackMyGroceries</Trans>
            </span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="container max-w-4xl mx-auto px-4 py-12">
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          <h1 className="font-display text-4xl font-bold mb-2">
            <Trans>Terms of Service</Trans>
          </h1>
          <p className="text-muted-foreground mb-8">{t`Last updated: ${lastUpdated}`}</p>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Agreement to Terms</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  By accessing or using TrackMyGroceries (the &quot;Service&quot;), you agree to be
                  bound by these Terms of Service (&quot;Terms&quot;). If you do not agree to these
                  Terms, please do not use the Service.
                </Trans>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>
                  These Terms constitute a legally binding agreement between you and
                  TrackMyGroceries (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;).
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Description of Service</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  TrackMyGroceries is a food inventory management application that helps you:
                </Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>
                  <Trans>Track food items and their expiry dates</Trans>
                </li>
                <li>
                  <Trans>Scan barcodes to quickly add products</Trans>
                </li>
                <li>
                  <Trans>Share household inventories with family members</Trans>
                </li>
                <li>
                  <Trans>Receive notifications about expiring items</Trans>
                </li>
                <li>
                  <Trans>Analyze consumption and waste patterns</Trans>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Account Registration</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>To use the Service, you must create an account. You agree to:</Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>
                  <Trans>Provide accurate and complete information</Trans>
                </li>
                <li>
                  <Trans>Maintain the security of your account credentials</Trans>
                </li>
                <li>
                  <Trans>Promptly update any changes to your information</Trans>
                </li>
                <li>
                  <Trans>Accept responsibility for all activities under your account</Trans>
                </li>
                <li>
                  <Trans>Notify us immediately of any unauthorized use</Trans>
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>
                  You must be at least 13 years old to create an account. If you are under 18, you
                  must have permission from a parent or guardian.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Acceptable Use</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>You agree not to:</Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>
                  <Trans>Use the Service for any illegal purpose</Trans>
                </li>
                <li>
                  <Trans>Attempt to gain unauthorized access to any part of the Service</Trans>
                </li>
                <li>
                  <Trans>Interfere with or disrupt the Service or servers</Trans>
                </li>
                <li>
                  <Trans>Upload malicious code or content</Trans>
                </li>
                <li>
                  <Trans>Impersonate another person or entity</Trans>
                </li>
                <li>
                  <Trans>
                    Scrape, harvest, or collect data from the Service without permission
                  </Trans>
                </li>
                <li>
                  <Trans>Use the Service to send spam or unsolicited communications</Trans>
                </li>
                <li>
                  <Trans>Circumvent any security measures or access controls</Trans>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Household Sharing</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  The Service allows you to create households and share inventory data with other
                  users. When using household features:
                </Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>
                  <Trans>
                    You are responsible for managing who has access to your household data
                  </Trans>
                </li>
                <li>
                  <Trans>
                    Household owners and admins can invite, remove, or change roles of members
                  </Trans>
                </li>
                <li>
                  <Trans>All household members can view and modify shared inventory data</Trans>
                </li>
                <li>
                  <Trans>
                    You should only invite people you trust to access your household information
                  </Trans>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Premium Subscriptions</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  Some features of the Service require a paid subscription (&quot;Premium&quot;).
                </Trans>
              </p>

              <h3 className="font-semibold text-lg mt-6 mb-3">
                <Trans>Billing</Trans>
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <Trans>Subscriptions are billed in advance on a monthly or annual basis</Trans>
                </li>
                <li>
                  <Trans>
                    Payments are processed by Paddle (web) or through your app store (iOS/Android)
                  </Trans>
                </li>
                <li>
                  <Trans>Prices are in your local currency and include applicable taxes</Trans>
                </li>
              </ul>

              <h3 className="font-semibold text-lg mt-6 mb-3">
                <Trans>Cancellation</Trans>
              </h3>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                <li>
                  <Trans>You may cancel your subscription at any time</Trans>
                </li>
                <li>
                  <Trans>Cancellation takes effect at the end of the current billing period</Trans>
                </li>
                <li>
                  <Trans>
                    You will retain access to Premium features until the end of your paid period
                  </Trans>
                </li>
                <li>
                  <Trans>
                    Refunds are handled according to the policies of Paddle or your app store
                  </Trans>
                </li>
              </ul>

              <h3 className="font-semibold text-lg mt-6 mb-3">
                <Trans>Price Changes</Trans>
              </h3>
              <p className="text-muted-foreground">
                <Trans>
                  We may change subscription prices with 30 days&apos; notice. Price changes will
                  apply to your next billing cycle after the notice period.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>User Content</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  You retain ownership of content you create in the Service (such as custom product
                  names, notes, and household data). By using the Service, you grant us a limited
                  license to:
                </Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>
                  <Trans>Store and display your content to you and your household members</Trans>
                </li>
                <li>
                  <Trans>Create backups of your data</Trans>
                </li>
                <li>
                  <Trans>
                    Use anonymized, aggregated data to improve the Service (your personal
                    information is never shared)
                  </Trans>
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Third-Party Services</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>The Service integrates with third-party services including:</Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>
                  <Trans>
                    <strong>Open Food Facts:</strong> Product database for barcode lookups. Data
                    from Open Food Facts is provided under the Open Database License.
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <strong>Payment processors:</strong> Paddle and app stores process payments
                    according to their own terms.
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <strong>PostHog:</strong> Product analytics and feature flags for improving the
                    app experience. No personally identifiable information is shared.
                  </Trans>
                </li>
                <li>
                  <Trans>
                    <strong>DeepL:</strong> Machine translation of product names and UI text into
                    your preferred language.
                  </Trans>
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>
                  We are not responsible for the content, accuracy, or practices of third-party
                  services.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Disclaimers</Trans>
              </h2>

              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 mb-4">
                <h3 className="font-semibold text-amber-600 dark:text-amber-400 mb-2">
                  <Trans>Not Medical or Nutritional Advice</Trans>
                </h3>
                <p className="text-sm text-muted-foreground">
                  <Trans>
                    TrackMyGroceries is a food inventory tool, not a source of medical or
                    nutritional advice. Expiry date tracking is for convenience only. Always use
                    your own judgment and check food safety guidelines when determining whether food
                    is safe to consume.
                  </Trans>
                </p>
              </div>

              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                <h3 className="font-semibold text-amber-600 dark:text-amber-400 mb-2">
                  <Trans>Data Accuracy</Trans>
                </h3>
                <p className="text-sm text-muted-foreground">
                  <Trans>
                    Product information from barcode lookups comes from community-contributed
                    databases and may not be complete or accurate. Always verify important
                    information (such as allergens) from the actual product packaging.
                  </Trans>
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Limitation of Liability</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  To the maximum extent permitted by law, TrackMyGroceries and its affiliates,
                  officers, employees, and agents shall not be liable for:
                </Trans>
              </p>
              <ul className="list-disc pl-6 text-muted-foreground space-y-2 mt-4">
                <li>
                  <Trans>Any indirect, incidental, special, or consequential damages</Trans>
                </li>
                <li>
                  <Trans>Loss of profits, data, or goodwill</Trans>
                </li>
                <li>
                  <Trans>Service interruptions or data loss</Trans>
                </li>
                <li>
                  <Trans>Any decision made based on information from the Service</Trans>
                </li>
                <li>
                  <Trans>Food spoilage, waste, or illness</Trans>
                </li>
              </ul>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>
                  Our total liability for any claims arising from your use of the Service shall not
                  exceed the amount you paid us in the 12 months preceding the claim, or $100 CAD,
                  whichever is greater.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Warranty Disclaimer</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT
                  WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
                  WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                  NON-INFRINGEMENT.
                </Trans>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>
                  We do not warrant that the Service will be uninterrupted, error-free, or secure,
                  or that any defects will be corrected.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Termination</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  We may suspend or terminate your account if you violate these Terms or for any
                  other reason at our discretion. You may delete your account at any time through
                  the app settings.
                </Trans>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>
                  Upon termination, your right to use the Service will immediately cease. Sections
                  of these Terms that by their nature should survive termination (such as limitation
                  of liability) will remain in effect.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Changes to Terms</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  We may modify these Terms at any time. We will notify you of material changes by
                  posting the updated Terms and changing the &quot;Last updated&quot; date. For
                  significant changes, we may also notify you via email or in-app notification.
                </Trans>
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                <Trans>
                  Your continued use of the Service after changes take effect constitutes acceptance
                  of the new Terms.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Governing Law</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>
                  These Terms are governed by the laws of the Province of Ontario, Canada, without
                  regard to conflict of law principles. Any disputes arising from these Terms or
                  your use of the Service shall be resolved in the courts of Ontario, Canada.
                </Trans>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="font-display text-2xl font-semibold mb-4">
                <Trans>Contact Us</Trans>
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                <Trans>If you have questions about these Terms, please contact us:</Trans>
              </p>
              <div className="mt-4 rounded-xl border p-4">
                <p className="font-semibold">TrackMyGroceries</p>
                <p className="text-muted-foreground">
                  <Trans>Email:</Trans>{' '}
                  <a
                    href="mailto:privacy@trackmygroceries.com"
                    className="text-primary hover:underline"
                  >
                    privacy@trackmygroceries.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container max-w-4xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} TrackMyGroceries. All rights reserved.</p>
          <div className="mt-2 space-x-4">
            <Link to="/privacy" className="hover:text-foreground">
              <Trans>Privacy Policy</Trans>
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              <Trans>Terms of Service</Trans>
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
